import React from 'react';
import { HeartCrack, Brain, Cloud, ArrowRight, Check } from 'lucide-react';

const issues = [
  {
    name: 'Physical Pain',
    icon: HeartCrack,
    description: 'Biblical principles for health and healing. Find hope and practical wisdom for physical challenges.',
    features: [
      'Divine healing truths',
      'Health & wellness principles',
      'Medical & spiritual integration',
      'Testimonies of healing'
    ],
    color: 'from-rose-500 to-red-600',
    bgColor: 'from-rose-50 to-red-50'
  },
  {
    name: 'Emotional Trauma',
    icon: Brain,
    description: 'Inner healing and restoration of the soul. Journey from pain to wholeness through Christ.',
    features: [
      'Trauma recovery paths',
      'Inner healing ministry',
      'Emotional restoration',
      'Professional resources'
    ],
    color: 'from-violet-500 to-purple-600',
    bgColor: 'from-violet-50 to-purple-50'
  },
  {
    name: 'Depression',
    icon: Cloud,
    description: 'Finding hope and purpose in difficult seasons. You are not alone in this journey.',
    features: [
      'Hope-filled resources',
      'Community support',
      'Spiritual warfare',
      'Professional guidance'
    ],
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'from-blue-50 to-indigo-50'
  }
];

const IssuesSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 bg-rose-50 text-rose-600 rounded-full text-sm font-medium mb-4">
            Addressing Life&apos;s Challenges
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Find healing, hope, and
            <span className="bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent"> restoration</span>
          </h2>
          <p className="text-lg text-slate-600">
            We understand that life brings challenges. Our resources address the whole person - body, soul, and spirit.
          </p>
        </div>

        {/* Issue cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {issues.map((issue, index) => {
            const Icon = issue.icon;
            return (
              <div
                key={issue.name}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${issue.bgColor} rounded-3xl transform group-hover:scale-105 transition-transform duration-300`} />
                <div className="relative bg-white rounded-3xl border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  {/* Header */}
                  <div className={`bg-gradient-to-r ${issue.color} p-6`}>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{issue.name}</h3>
                    </div>
                  </div>
                  
                  {/* Body */}
                  <div className="p-6">
                    <p className="text-slate-600 mb-6">{issue.description}</p>
                    
                    <div className="space-y-3">
                      {issue.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <button className={`mt-6 w-full py-3 bg-gradient-to-r ${issue.color} text-white font-medium rounded-xl hover:shadow-lg transition-shadow flex items-center justify-center gap-2 group/btn`}>
                      Find Help
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Support banner */}
        <div className="mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Need immediate support?</h3>
              <p className="text-slate-400">Our prayer team is available 24/7 to pray with you and provide encouragement.</p>
            </div>
            <button className="px-8 py-4 bg-white text-slate-900 font-semibold rounded-full hover:bg-slate-100 transition-colors whitespace-nowrap">
              Request Prayer Now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IssuesSection;
